import { Sort } from '@angular/material/sort';
import {
  ActionDefinition,
  BasicStatementV2,
  Cadence,
  ClientGQLDisplay,
  CollectionsEmailSettings,
  CollectionsSequence,
  CollectionsThrottleSettings,
  CollectionsTriggersSettings,
  CommunicationRecipientRow,
  DynamicField,
  ExclusionStatusUpdate,
  RecipientOutstandingAggregates,
  SequenceAggregate,
  SmartStatementActionDefinitionJson,
  SmartStatementBucketSetting,
  StaffGQL,
  Statement,
} from '@anduin/api';
import { DEFAULT_LOADABLE_STATE, Loadable, LoadableStatus, PagedDataModel } from '@anduin/shared';
import { StatementRecipientModel } from '../models';

export const DEFAULT_RECIPIENT_PAGE_LIMIT = 20;

export interface SortOption {
  field: string;
  direction: 'asc' | 'desc';
}

export interface CommonCollectionsStateModelPagedExtraParameters {
  partner: string;
  sortOption: SortOption;
}

export interface CommonCollectionsStateModel {
  /** Feature > Sidebar */
  recipients: StatementRecipientModel[];
  // List of paged data without pagination (vertical scroll and load / load more)
  pagedRecipientResults: PagedDataModel<number>;
  pagedExtraParameters?: CommonCollectionsStateModelPagedExtraParameters;

  // Client results specifically for searches; they fall out of sync with the paged data and are kept separately. Search class is anonymous because its not used anywhere but the state and never directly named.
  recipientSearchResults: RecipientSearchResult[];
  selectedStatementRecipientId?: number; // v1: Client ID, v2: Recipient ID

  /** Feature > Content */
  aggregates: Loadable<RecipientOutstandingAggregates>[];
  // Statements grouped by recipient. In v1, this is specifically PAST statements (sent/skipped). In v2, this is all statements.
  statements: Loadable<CollectionsStatements>[];
  reviewStatements: Loadable<Statement>[];
  statementSkipRequests: Loadable<undefined>[];
  statementApproveRequests: Loadable<undefined>[];
  statementUndoRequests: Loadable<undefined>[];

  /** Settings v1 */
  cadences: Loadable<Cadence[]>;
  sequenceAggregates: Loadable<SequenceAggregate[]>[];
  sequences: Loadable<SequenceWrapper>[];
  cadenceUpdateRequestStatus: LoadableStatus;
  sequenceUpdateRequestStatus: LoadableStatus;
  messageSettingsUpdateRequestStatus: LoadableStatus;
  messageSettings: Loadable<ActionDefinition<SmartStatementActionDefinitionJson>>;
  smartStatementBucketSettings: Loadable<SmartStatementBucketSetting[]>;
  messageVariables: Loadable<DynamicField[]>;
  // lookup for matching the ID of the staff member that last updates a client's exclusion status (to display name in table)
  staff: Loadable<StaffGQL[]>;
  // List of paged data without pagination (vertical scroll and load / load more)
  excludedClients: PagedDataModel<ClientGQLDisplay | CommunicationRecipientRow>;
  // column and direction of last page request with an active sort
  latestExcludedClientsSort?: Sort;
  // Client search results; they fall out of sync with the paged data and are kept separately.
  excludedClientSearchResults: {
    term: string;
    results: Loadable<ClientGQLDisplay[] | CommunicationRecipientRow[]>;
    // Request date in Millis since Epoch
    requestedAt: number;
  }[];
  // number of excluded clients in the tenant's DB table
  alreadyExcludedClientsTotal: Loadable<number>;
  // exclusion and inclusion update requests (can be either or both, based on user inputs)
  exclusionStatusUpdates?: Loadable<ExclusionStatusUpdate[]>;

  /** Settings v2 */
  settings: CollectionsSettingsStateModel;
}

export interface CollectionsSettingsStateModel {
  throttling: Loadable<CollectionsThrottleSettings>;
  triggers: Loadable<CollectionsTriggersSettings>;
  excludedRecipientIds: Loadable<number[]>;
  autoApprove: Loadable<boolean>;
  email: Loadable<CollectionsEmailSettings>;
  updateStatus?: LoadableStatus;
}

export const DEFAULT_PAGE_RECIPIENT_RESULTS = {
  limit: DEFAULT_RECIPIENT_PAGE_LIMIT,
  pages: [],
};

export const COMMON_COLLECTIONS_STATE_MODEL_DEFAULT: CommonCollectionsStateModel = {
  recipients: [],
  pagedRecipientResults: DEFAULT_PAGE_RECIPIENT_RESULTS,
  recipientSearchResults: [],
  staff: { status: LoadableStatus.DEFAULT, data: [] },
  excludedClients: {
    limit: DEFAULT_RECIPIENT_PAGE_LIMIT,
    pages: [],
  },
  excludedClientSearchResults: [],
  alreadyExcludedClientsTotal: { status: LoadableStatus.DEFAULT, data: null },
  aggregates: [],
  statements: [],
  reviewStatements: [],
  cadences: { status: LoadableStatus.DEFAULT, data: [] },
  sequenceAggregates: [],
  sequences: [],
  cadenceUpdateRequestStatus: LoadableStatus.DEFAULT,
  sequenceUpdateRequestStatus: LoadableStatus.DEFAULT,
  messageSettingsUpdateRequestStatus: LoadableStatus.DEFAULT,
  messageSettings: { status: LoadableStatus.DEFAULT },
  smartStatementBucketSettings: { status: LoadableStatus.DEFAULT },
  messageVariables: { status: LoadableStatus.DEFAULT, data: [] },
  statementSkipRequests: [],
  statementApproveRequests: [],
  statementUndoRequests: [],

  /** Collections v2 */
  settings: {
    throttling: DEFAULT_LOADABLE_STATE,
    triggers: DEFAULT_LOADABLE_STATE,
    excludedRecipientIds: DEFAULT_LOADABLE_STATE,
    autoApprove: DEFAULT_LOADABLE_STATE,
    email: DEFAULT_LOADABLE_STATE,
    updateStatus: LoadableStatus.DEFAULT,
  },
};

export interface SequenceWrapper {
  sequenceId: number;
  sequence?: CollectionsSequence;
}

export interface RecipientSearchResult {
  term: string;
  partnerName?: string;
  results: Loadable<number[]>;
  requestedAt: number; // request date in ms since Epoch
}

export type CollectionsStatements = Statement[] | BasicStatementV2[];
